import * as React from 'react'
import { RouteComponentProps, browserHistory } from 'react-router'
import { CurrentUserStore } from '../../../stores/CurrentUserStore'
import { CurrentCompanyStore } from '../../../stores/CurrentCompanyStore'
import { observer, inject } from 'mobx-react'
import { Filter, InvoiceFilter } from './FilterStore'
import { observable, action, computed } from 'mobx'
import {
  API,
  IPaginatedInvoice,
  InvoiceStatus,
  IBaseInvoice,
  PermissionNames,
} from '@getgreenline/homi-shared'
import { message } from 'antd'
import { parseAndDownloadCSV, parseErrorMsg } from '../../../utilities/helpers'
import { NoPermissionsResult } from '../../../components/NoPermissionsResult'
import { InvoiceList } from './InvoiceList/Main/InvoiceList'
import moment from 'moment'

export class CreateInvoiceList {
  invoiceCountPerPage = 30

  @observable
  paginatedInvoices: IPaginatedInvoice = {
    total: 0,
    limit: 0,
    offset: 0,
    data: [],
  }

  constructor(private currentCompanyStore: CurrentCompanyStore) {}

  @action
  fetchInvoices = async (invoiceFilter: InvoiceFilter): Promise<IPaginatedInvoice> =>
    API.getInvoices(this.currentCompanyStore.company!.id, invoiceFilter)

  @action
  exportInvoiceLots = async () => API.exportInvoiceLots(this.currentCompanyStore.company!.id)

  @action
  setPaginatedInvoices = (paginatedInvoices: IPaginatedInvoice) => {
    this.paginatedInvoices = paginatedInvoices
  }

  renderInvoiceStatus = (status: InvoiceStatus) => {
    const invoiceStatusObject = {
      invoiceStatusName: '',
      invoiceStatusColor: '',
    }

    if (status === InvoiceStatus.PENDING) {
      invoiceStatusObject.invoiceStatusName = 'Pending'
      invoiceStatusObject.invoiceStatusColor = 'silver'
    } else if (status === InvoiceStatus.IN_PROGRESS) {
      invoiceStatusObject.invoiceStatusName = 'In progress'
      invoiceStatusObject.invoiceStatusColor = 'orange'
    } else if (status === InvoiceStatus.COMPLETED) {
      invoiceStatusObject.invoiceStatusName = 'Completed'
      invoiceStatusObject.invoiceStatusColor = 'green'
    } else {
      invoiceStatusObject.invoiceStatusName = 'Cancelled'
      invoiceStatusObject.invoiceStatusColor = 'red'
    }

    return invoiceStatusObject
  }

  renderPaidStatus = (invoice: IBaseInvoice) => {
    if (invoice.isReturn) {
      return {
        paidStatusName: 'Returned',
        paidStatusColor: 'silver',
      }
    }

    if (invoice.paid === 0) {
      return {
        paidStatusName: 'Unpaid',
        paidStatusColor: 'red',
      }
    }

    if (invoice.paid < invoice.total) {
      return {
        paidStatusName: 'Partial',
        paidStatusColor: 'orange',
      }
    }

    if (invoice.paid >= invoice.total) {
      return {
        paidStatusName: 'Paid',
        paidStatusColor: 'silver',
      }
    }

    return {
      paidStatusName: undefined,
      paidStatusColor: undefined,
    }
  }

  @computed
  get invoicesInCsv() {
    return this.paginatedInvoices.data.map((invoice) => {
      return {
        ID: invoice.customId,
        Location: invoice.locationName,
        Vendor: invoice.supplierName,
        Title: invoice.title,
        Employee: invoice.startingUserName,
        Created: moment(invoice.createDate).format('MMM Do YYYY'),
        Completed: invoice.completeDate ? moment(invoice.completeDate).format('MMM Do YYYY') : '-',
        Total: invoice.total / 100,
        Status: this.renderInvoiceStatus(invoice.status).invoiceStatusName,
      }
    })
  }

  @computed
  get currentPage(): number {
    const { offset, limit } = this.paginatedInvoices
    const currPage = offset / limit

    return isNaN(currPage) ? 0 : currPage
  }

  @computed
  get numberOfPages(): number {
    const { total, limit } = this.paginatedInvoices
    const pageCount = Math.ceil(total / limit)

    return isNaN(pageCount) ? 0 : pageCount
  }
}

interface Props extends RouteComponentProps<{}, {}> {
  currentUserStore: CurrentUserStore
  currentCompanyStore: CurrentCompanyStore
}

interface State {
  isLoading: boolean
}

@inject('currentCompanyStore', 'currentUserStore')
@observer
export class DashboardInvoices extends React.Component<Props, State> {
  createInvoiceList = new CreateInvoiceList(this.props.currentCompanyStore)

  invoiceFilter = new Filter(browserHistory)

  company = this.props.currentCompanyStore.company!

  constructor(props: Props) {
    super(props)

    this.state = {
      isLoading: false,
    }
  }

  componentDidMount = async () => {
    const currentCompanyStore = this.props.currentCompanyStore

    currentCompanyStore.getEntityUsers()
    currentCompanyStore.supplierStore!.getSuppliers()

    await this.loadInvoices()
  }

  loadInvoices = async (offset?: number) => {
    if (offset !== undefined) {
      this.invoiceFilter.offset = offset
    }

    try {
      this.setState({ isLoading: true })

      const paginatedInvoices = await this.createInvoiceList.fetchInvoices(
        this.invoiceFilter.networkObject,
      )

      this.createInvoiceList.setPaginatedInvoices(paginatedInvoices)

      this.invoiceFilter.apply()
    } catch (error) {
      message.error(parseErrorMsg(error))
    } finally {
      this.setState({ isLoading: false })
    }
  }

  exportInvoiceLots = async () => {
    try {
      const invoiceLots = await this.createInvoiceList.exportInvoiceLots()

      parseAndDownloadCSV('purchase-order-lots', invoiceLots.invoiceLines)

      message.success('Export successful')
    } catch (error) {
      message.error(parseErrorMsg(error))
    }
  }

  get canViewPurchaseOrders(): boolean {
    return this.props.currentUserStore!.hasPermissionAtEntity(
      PermissionNames.CAN_VIEW_PURCHASE_ORDERS,
      this.company.id,
    )
  }

  render() {
    return (
      <>
        {this.canViewPurchaseOrders ? (
          <InvoiceList
            createInvoiceList={this.createInvoiceList}
            invoiceFilter={this.invoiceFilter}
            isLoading={this.state.isLoading}
            loadInvoices={this.loadInvoices}
          />
        ) : (
          <NoPermissionsResult permissionName={PermissionNames.CAN_VIEW_PURCHASE_ORDERS} />
        )}
      </>
    )
  }
}
